import React, { useState, useEffect, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import LoadingScreen from './container/Loading';
import Header from './container/Header';
import './container/css/styles.css';
import './styles/inputs.css';

// Lazy load your components
const MainPage = lazy(() => import('./container/MainPage'));
const SignInPage = lazy(() => import('./container/SignInPage'));
const Dashboard = lazy(() => import('./container/Dashboard'));
const DashboardLayout = lazy(() => import('./container/DashboardLayout'));
const DashboardSettings = lazy(() => import('./container/DashboardSettings'));
const AvailableSoon = lazy(() => import('./container/AvailableSoon'));
const Calendar = lazy(() => import('./container/Calendar'));
const Monthly = lazy(() => import('./container/Monthly'));
const SignUp = lazy(() => import('./container/SignUp'));
const Weekly = lazy(() => import('./container/Weekly'));
const Profile = lazy(() => import('./container/Profile'));
const DashboardTeams = lazy(() => import('./container/DashboardTeams'));
const AdminSettings = lazy(() => import('./container/AdminSettings'));
const TeamSettingsPage = lazy(() => import('./container/TeamSettingsPage'));
const ForgottenPassword = lazy(() => import('./container/ForgottenPassword'));
const Salaries = lazy(() => import('./container/Salary'));
const Subscribe = lazy(() => import('./container/Subscribe'));
const Test = lazy(() => import('./container/test'));
const RedirectToTeamSettings = lazy(() =>
  import('./container/RedirectToTeamSettings').then((m) => ({ default: m.RedirectToTeamSettings }))
);

function AppContent() {
  const [isNightMode, setIsNightMode] = useState(() => {
    const storedValue = localStorage.getItem('isNightMode');
    return storedValue !== null ? JSON.parse(storedValue) : true;
  });

  const [isOnline, setIsOnline] = useState(navigator.onLine); // Track online status

  const toggleNightMode = () => {
    const newMode = !isNightMode;
    setIsNightMode(newMode);
    localStorage.setItem('isNightMode', JSON.stringify(newMode));
  };

  useEffect(() => {
    const handleOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    // Clean up event listeners
    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  /* Sync theme to html so body and global bg use same tokens as dashboard */
  useEffect(() => {
    document.documentElement.className = isNightMode ? 'night' : 'day';
    document.documentElement.setAttribute('data-theme', isNightMode ? 'dark' : 'light');
  }, [isNightMode]);

  if (!isOnline) {
    return <LoadingScreen />; // Show loading screen when offline
  }

  return (
    <>
      <Header toggleNightMode={toggleNightMode} isNightMode={isNightMode} />
      <main className="app-main">
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
          <Route path="/" element={<MainPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/confirmation" element={<SignInPage />} />
          <Route path="/forgot-password" element={<ForgottenPassword />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/test" element={<Test />} />
          {/* Dashboard nested routes (plan); old paths redirect */}
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="settings" element={<Navigate to="/dashboard/profile" replace />} />
            <Route path="schedule" element={<Calendar />} />
            <Route path="team-schedule" element={<Monthly />} />
            <Route path="weekly" element={<Weekly />} />
            <Route path="team/:teamId" element={<TeamSettingsPage />} />
            <Route path="rates" element={<Salaries />} />
            <Route path="profile" element={<Profile />} />
            <Route path="teams" element={<DashboardTeams />} />
            <Route path="ai-schedule" element={<AvailableSoon />} />
            <Route path="functionalities" element={<AvailableSoon />} />
            <Route path="onboarding-rating" element={<AvailableSoon />} />
            <Route path="generate-test-members" element={<AvailableSoon />} />
          </Route>
          {/* Legacy redirects to new dashboard paths */}
          <Route path="/calendar" element={<Navigate to="/dashboard/schedule" replace />} />
          <Route path="/monthly" element={<Navigate to="/dashboard/team-schedule" replace />} />
          <Route path="/weekly" element={<Navigate to="/dashboard/weekly" replace />} />
          <Route path="/profile" element={<Navigate to="/dashboard/profile" replace />} />
          <Route path="/admin-settings" element={<RedirectToTeamSettings />} />
          <Route path="/salaries" element={<Navigate to="/dashboard/rates" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
    </>
  );
}

function App() {
  return (
    <Router>
      <I18nextProvider i18n={i18n}>
        <AppContent />
      </I18nextProvider>
    </Router>
  );
}

export default App;
